// src/Components/Documents/Vault.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  LinearProgress,
  Alert,
  Menu,
  MenuItem,
  Tooltip,
  Snackbar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  Divider,
  Avatar,
  Select,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Breadcrumbs,
  Link,
  Chip,
  useMediaQuery,
  useTheme,
  ToggleButton,
  ToggleButtonGroup,
  Card,
  CardContent,
  CardActions,
  Grid,
  InputAdornment
} from '@mui/material';
import {
  Upload as UploadIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Share as ShareIcon,
  Folder as FolderIcon,
  InsertDriveFile as FileIcon,
  MoreVert as MoreVertIcon,
  CreateNewFolder as CreateFolderIcon,
  Person as PersonIcon,
  Group as GroupIcon,
  Lock as LockIcon,
  Home as HomeIcon,
  NavigateNext as NavigateNextIcon,
  CloudUpload as CloudUploadIcon,
  Edit as EditIcon,
  ViewList as ViewListIcon,
  ViewModule as ViewModuleIcon,
  ViewComfy as ViewComfyIcon,
  Image as ImageIcon,
  Search as SearchIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  DriveFileMove as MoveIcon,
  ContentCopy as CopyIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useProfile } from '../../contexts/ProfileContext';
import { documentService } from '../../services/documentService';
import { uploadToFamilyDocumentsWithPath, isFamilyAdmin } from '../../services/familyDocumentService';
import { collection, getDocs } from 'firebase/firestore';
import { db, storage } from '../../firebase';
import { ref as storageRef, getBlob } from 'firebase/storage';
import { downloadFile } from '../../utils/downloadUtils';
import ImageLightbox from './ImageLightbox';
import FolderPickerDialog from './FolderPickerDialog';
import logger from '../../utils/logger';

const extractStoragePathFromUrl = (url) => {
  try {
    const m = url && url.match(/\/o\/([^?]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  } catch {
    return null;
  }
};

/**
 * Helper: Get user-friendly file type label
 */
const getFileTypeLabel = (doc) => {
  // Try to extract extension from filename first
  const fileName = doc.fileName || doc.title || doc.name || '';
  const extension = fileName.split('.').pop()?.toLowerCase();

  if (extension && extension !== fileName.toLowerCase()) {
    return extension.toUpperCase();
  }

  // Fallback: Map MIME types to friendly names
  const mimeType = doc.fileType || doc.mimeType || '';
  const mimeMap = {
    'application/pdf': 'PDF',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PowerPoint',
    'application/msword': 'Word',
    'application/vnd.ms-excel': 'Excel',
    'application/vnd.ms-powerpoint': 'PowerPoint',
    'text/plain': 'Text',
    'image/jpeg': 'JPEG',
    'image/png': 'PNG',
    'image/gif': 'GIF',
    'image/svg+xml': 'SVG'
  };

  if (mimeMap[mimeType]) {
    return mimeMap[mimeType];
  }

  // Final fallback: extract after slash
  const shortType = mimeType.split('/')[1];
  return shortType && shortType.length < 10 ? shortType.toUpperCase() : 'File';
};

/**
 * Vault Component
 *
 * Secure document vault with granular sharing capabilities
 * Features:
 * - Upload documents to vault
 * - Share documents with specific members or groups
 * - Download vault documents
 * - Delete vault documents
 * - Folder organization
 * - Customizable for Photos (via fileTypeFilter prop)
 *
 * Uses documentService.js for all operations
 */
const Vault = ({
  fileTypeFilter = null,           // Filter by MIME type (e.g., 'image/*' for photos)
  defaultView = 'list',             // Default view mode: 'list', 'grid', or 'gallery'
  title = 'Vault',                  // Page title
  uploadButtonText = 'Upload',      // Upload button text
  emptyMessage = 'No documents in vault yet. Upload your first document to get started!'
} = {}) => {
  const { currentUser } = useAuth();
  const { profile } = useProfile();

  // Mobile responsive hooks
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [isAdmin, setIsAdmin] = useState(false);
  useEffect(() => {
    const checkAdmin = async () => {
      if (currentUser && profile?.familyCode) {
        const adminStatus = await isFamilyAdmin(profile.familyCode, currentUser.uid);
        setIsAdmin(adminStatus);
      }
    };
    checkAdmin();
  }, [currentUser, profile]);

  // State management
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [currentPath, setCurrentPath] = useState('/');
  const [isDragging, setIsDragging] = useState(false);
  const [viewMode, setViewMode] = useState(() => {
    // Use different localStorage key for photos vs documents
    const storageKey = fileTypeFilter === 'image/*' ? 'photosVaultViewMode' : 'vaultViewMode';
    return localStorage.getItem(storageKey) || defaultView;
  });

  // Family members and groups
  const [familyMembers, setFamilyMembers] = useState([]);
  const [familyGroups, setFamilyGroups] = useState([]);

  // Dialog states
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [manageAccessDialogOpen, setManageAccessDialogOpen] = useState(false);
  const [folderDialogOpen, setFolderDialogOpen] = useState(false);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadCategory, setUploadCategory] = useState('general');
  const [newFolderName, setNewFolderName] = useState('');
  const [renameValue, setRenameValue] = useState('');

  // Sharing state
  const [selectedMembersToShare, setSelectedMembersToShare] = useState([]);
  const [selectedGroupsToShare, setSelectedGroupsToShare] = useState([]);
  const [sharePermission, setSharePermission] = useState('read');
  const [currentShares, setCurrentShares] = useState([]);

  // Menu state
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuItem, setMenuItem] = useState(null);

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Search and sort state
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('date'); // 'name', 'date', 'size', 'type'
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc', 'desc'

  // Image lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  /**
   * Load family members for sharing
   */
  const loadFamilyMembers = useCallback(async () => {
    if (!profile?.familyCode) return;

    try {
      const membersRef = collection(db, 'families', profile.familyCode, 'members');
      const membersSnapshot = await getDocs(membersRef);
      const members = membersSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(member => member.id !== currentUser.uid); // Exclude current user

      setFamilyMembers(members);
    } catch (err) {
      logger.error('Error loading family members:', err);
    }
  }, [profile, currentUser]);

  /**
   * Load family groups for sharing
   */
  const loadFamilyGroups = useCallback(async () => {
    if (!profile?.familyCode) return;

    try {
      const groupsRef = collection(db, 'families', profile.familyCode, 'groups');
      const groupsSnapshot = await getDocs(groupsRef);
      const groups = groupsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      setFamilyGroups(groups);
    } catch (err) {
      logger.error('Error loading family groups:', err);
    }
  }, [profile]);

  /**
   * Load vault documents
   */
  const loadDocuments = useCallback(async () => {
    if (!currentUser || !profile?.familyCode) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      let docs = await documentService.listVaultDocuments(
        profile.familyCode,
        currentUser.uid,
        currentPath
      );

      // Apply fileTypeFilter if provided (e.g., 'image/*' for photos)
      if (fileTypeFilter) {
        docs = docs.filter(doc => {
          // Always show folders
          if (doc.type === 'folder') return true;

          const mimeType = doc.fileType || doc.mimeType || '';

          // Handle wildcard filters (e.g., 'image/*')
          if (fileTypeFilter.includes('/*')) {
            const filterPrefix = fileTypeFilter.split('/*')[0];
            return mimeType.startsWith(filterPrefix + '/');
          }

          // Exact MIME type match
          return mimeType === fileTypeFilter;
        });
      }

      setDocuments(docs);
    } catch (err) {
      logger.error('Error loading vault documents:', err);
      setError(err.message || 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [currentUser, profile, currentPath, fileTypeFilter]);

  // Load data on mount
  useEffect(() => {
    loadDocuments();
    loadFamilyMembers();
    loadFamilyGroups();
  }, [loadDocuments, loadFamilyMembers, loadFamilyGroups]);

  /**
   * Generate breadcrumb segments from current path
   */
  const getBreadcrumbs = () => {
    const segments = currentPath.split('/').filter(Boolean);
    return segments;
  };

  /**
   * Navigate to breadcrumb segment
   */
  const handleBreadcrumbClick = (index) => {
    const segments = getBreadcrumbs();
    const newPath = '/' + segments.slice(0, index + 1).join('/');
    setCurrentPath(newPath);
  };

  /**
   * Handle drag & drop events
   */
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set dragging to false if leaving the document
    if (e.target === e.currentTarget) {
      setIsDragging(false);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      // Upload files directly to current folder
      await handleUpload(files);
    }
  };

  /**
   * Handle file upload
   */
  const handleUpload = async (files) => {
    if (!files || files.length === 0) return;

    try {
      setUploading(true);
      setUploadProgress(0);

      for (let i = 0; i < files.length; i++) {
        const file = files[i];

        await documentService.uploadVaultDocument(
          profile.familyCode,
          currentUser.uid,
          file,
          currentPath,
          (progress) => {
            const fileProgress = (i / files.length) + (progress / files.length);
            setUploadProgress(Math.round(fileProgress * 100));
          },
          uploadCategory
        );
      }

      setSnackbar({
        open: true,
        message: `Successfully uploaded ${files.length} file(s) to vault`,
        severity: 'success'
      });

      await loadDocuments();
      setUploadDialogOpen(false);
      setSelectedFiles([]);
      setUploadCategory('general');
    } catch (err) {
      logger.error('Error uploading files:', err);
      setSnackbar({
        open: true,
        message: err.message || 'Failed to upload files',
        severity: 'error'
      });
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDownload = async (document) => {
    const filename = document.fileName || document.title || document.name || 'download';
    const url = document.fileUrl || document.downloadURL;
    const storagePath = document.storagePath || extractStoragePathFromUrl(url);
    setSnackbar({ open: true, message: `Downloading ${filename}…`, severity: 'info' });
    try {
      await downloadFile(url, filename, storagePath);
      setSnackbar({ open: true, message: `"${filename}" downloaded`, severity: 'success' });
    } catch (err) {
      logger.error('Error downloading file:', err);
      setSnackbar({ open: true, message: err.message || 'Failed to download file', severity: 'error' });
    }
  };

  /**
   * Handle file deletion
   */
  const handleDelete = async () => {
    if (!selectedFile) return;

    try {
      await documentService.deleteVaultDocument(
        profile.familyCode,
        currentUser.uid,
        selectedFile.id
      );

      setSnackbar({
        open: true,
        message: `Deleted ${selectedFile.name}`,
        severity: 'success'
      });

      await loadDocuments();
      setDeleteDialogOpen(false);
      setSelectedFile(null);
    } catch (err) {
      logger.error('Error deleting file:', err);
      setSnackbar({
        open: true,
        message: err.message || 'Failed to delete file',
        severity: 'error'
      });
    }
  };

  /**
   * Handle document sharing
   */
  const handleShare = async () => {
    if (!selectedFile) return;

    try {
      // Prepare members and groups to share with
      const membersToShare = selectedMembersToShare;
      const groupsToShare = selectedGroupsToShare;

      if (membersToShare.length === 0 && groupsToShare.length === 0) {
        setSnackbar({
          open: true,
          message: 'Please select at least one member or group to share with',
          severity: 'warning'
        });
        return;
      }

      // Share with selected members
      if (membersToShare.length > 0) {
        await documentService.shareVaultDocument(
          profile.familyCode,
          currentUser.uid,
          selectedFile.id,
          {
            userIds: membersToShare,
            permission: sharePermission
          }
        );
      }

      // Share with selected groups (by sharing with all group members)
      if (groupsToShare.length > 0) {
        for (const groupId of groupsToShare) {
          const group = familyGroups.find(g => g.id === groupId);
          if (group && group.members) {
            await documentService.shareVaultDocument(
              profile.familyCode,
              currentUser.uid,
              selectedFile.id,
              {
                userIds: group.members,
                permission: sharePermission
              }
            );
          }
        }
      }

      setSnackbar({
        open: true,
        message: `Shared "${selectedFile.name}" successfully`,
        severity: 'success'
      });

      // Reset sharing state
      setShareDialogOpen(false);
      setSelectedFile(null);
      setSelectedMembersToShare([]);
      setSelectedGroupsToShare([]);
      setSharePermission('read');
    } catch (err) {
      logger.error('Error sharing document:', err);
      setSnackbar({
        open: true,
        message: err.message || 'Failed to share document',
        severity: 'error'
      });
    }
  };

  /**
   * Open manage access dialog and load current shares
   */
  const handleOpenManageAccess = async (document) => {
    setSelectedFile(document);

    // Build list of current shares with member details
    const shares = [];
    if (document.sharedWith && document.sharedWith.length > 0) {
      for (const userId of document.sharedWith) {
        const member = familyMembers.find(m => m.id === userId);
        if (member) {
          // Determine permission level
          let permission = 'read';
          if (document.permissions?.canEdit?.includes(userId)) {
            permission = 'edit';
          }
          if (document.permissions?.canShare?.includes(userId)) {
            permission = 'share';
          }

          shares.push({
            userId,
            userName: `${member.firstName} ${member.lastName}`,
            userEmail: member.email,
            permission
          });
        }
      }
    }

    setCurrentShares(shares);
    setManageAccessDialogOpen(true);
  };

  /**
   * Revoke access from a specific user
   */
  const handleRevokeAccess = async (userId) => {
    if (!selectedFile) return;

    try {
      await documentService.revokeVaultDocumentSharing(
        profile.familyCode,
        currentUser.uid,
        selectedFile.id,
        { userIds: [userId] }
      );

      setSnackbar({
        open: true,
        message: 'Access revoked successfully',
        severity: 'success'
      });

      // Update current shares list
      setCurrentShares(prev => prev.filter(share => share.userId !== userId));

      // Reload documents to update the table
      await loadDocuments();
    } catch (err) {
      logger.error('Error revoking access:', err);
      setSnackbar({
        open: true,
        message: err.message || 'Failed to revoke access',
        severity: 'error'
      });
    }
  };

  /**
   * Update permission level for a shared user
   */
  const handleUpdatePermission = async (userId, newPermission) => {
    if (!selectedFile) return;

    try {
      // First revoke all permissions
      await documentService.revokeVaultDocumentSharing(
        profile.familyCode,
        currentUser.uid,
        selectedFile.id,
        { userIds: [userId] }
      );

      // Then re-share with new permission
      await documentService.shareVaultDocument(
        profile.familyCode,
        selectedFile.id,
        [userId],
        newPermission
      );

      setSnackbar({
        open: true,
        message: 'Permission updated successfully',
        severity: 'success'
      });

      // Update current shares list
      setCurrentShares(prev => prev.map(share =>
        share.userId === userId
          ? { ...share, permission: newPermission }
          : share
      ));

      // Reload documents to update the table
      await loadDocuments();
    } catch (err) {
      logger.error('Error updating permission:', err);
      setSnackbar({
        open: true,
        message: err.message || 'Failed to update permission',
        severity: 'error'
      });
    }
  };

  /**
   * Handle folder creation
   */
  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;

    try {
      await documentService.createFolder(
        profile.familyCode,
        currentUser.uid,
        'vault',
        currentPath,
        newFolderName
      );

      setSnackbar({
        open: true,
        message: `Created folder "${newFolderName}"`,
        severity: 'success'
      });

      await loadDocuments();
      setFolderDialogOpen(false);
      setNewFolderName('');
    } catch (err) {
      logger.error('Error creating folder:', err);
      setSnackbar({
        open: true,
        message: err.message || 'Failed to create folder',
        severity: 'error'
      });
    }
  };

  /**
   * Handle folder navigation
   */
  const handleFolderClick = (folder) => {
    const folderName = folder.title || folder.name;
    const newPath = currentPath === '/' || currentPath === ''
      ? `/${folderName}`
      : `${currentPath}/${folderName}`;
    setCurrentPath(newPath);
  };

  /**
   * Handle rename file or folder
   */
  const handleRename = async () => {
    if (!renameValue.trim() || !selectedFile) return;

    try {
      const itemType = selectedFile.type === 'folder' ? 'folder' : 'file';

      await documentService.renameVaultItem(
        profile.familyCode,
        currentUser.uid,
        selectedFile.id,
        renameValue,
        itemType
      );

      setSnackbar({
        open: true,
        message: `Renamed to "${renameValue}"`,
        severity: 'success'
      });

      // Reload documents
      await loadDocuments();
      setRenameDialogOpen(false);
      setRenameValue('');
      setSelectedFile(null);
    } catch (err) {
      logger.error('Error renaming item:', err);
      setSnackbar({
        open: true,
        message: err.message || 'Failed to rename item',
        severity: 'error'
      });
    }
  };

  /**
   * Handle move file or folder to another folder
   */
  const handleMove = async (destinationPath) => {
    if (!selectedFile) return;

    try {
      await documentService.moveVaultDocument(
        profile.familyCode,
        currentUser.uid,
        selectedFile.id,
        destinationPath
      );

      const itemName = selectedFile.name || selectedFile.fileName || 'Item';
      const destDisplay = destinationPath === '/' ? 'Root' : destinationPath;

      setSnackbar({
        open: true,
        message: `Moved "${itemName}" to ${destDisplay}`,
        severity: 'success'
      });

      // Reload documents
      await loadDocuments();
      setMoveDialogOpen(false);
      setSelectedFile(null);
    } catch (err) {
      logger.error('Error moving item:', err);
      setSnackbar({
        open: true,
        message: err.message || 'Failed to move item',
        severity: 'error'
      });
    }
  };

  /**
   * Handle copy to family documents
   */
  const handleCopyToFamily = async (file) => {
    if (!file || file.type === 'folder') {
      setSnackbar({ open: true, message: 'Only files can be copied to Family Documents', severity: 'warning' });
      return;
    }

    try {
      const path = file.storagePath || extractStoragePathFromUrl(file.fileUrl || file.downloadURL);
      if (!path) throw new Error('Cannot resolve storage path for this file');
      const blob = await getBlob(storageRef(storage, path));
      const fileObj = new File([blob], file.fileName || file.name || file.title, { type: file.fileType || file.mimeType || blob.type });

      const storageType = (file.fileType || file.mimeType || '').startsWith('image/') ? 'photos' : 'documents';

      await uploadToFamilyDocumentsWithPath(profile.familyCode, fileObj, {
        category: file.category || 'general',
        uploadedBy: currentUser.uid,
        uploadedByName: profile?.displayName || `${profile?.firstName} ${profile?.lastName}` || currentUser.email,
        storageType
      }, '/');

      setSnackbar({ open: true, message: `"${file.fileName || file.name || file.title}" copied to Family Documents`, severity: 'success' });
    } catch (err) {
      logger.error('Error copying to family documents:', err);
      setSnackbar({ open: true, message: err.message || 'Failed to copy to Family Documents', severity: 'error' });
    }
  };

  /**
   * Handle menu open
   */
  const handleMenuOpen = (event, item) => {
    setMenuAnchor(event.currentTarget);
    setMenuItem(item);
  };

  /**
   * Handle menu close
   */
  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuItem(null);
  };

  /**
   * Handle view mode change
   */
  const handleViewModeChange = (event, newView) => {
    if (newView !== null) {
      setViewMode(newView);
      const storageKey = fileTypeFilter === 'image/*' ? 'photosVaultViewMode' : 'vaultViewMode';
      localStorage.setItem(storageKey, newView);
    }
  };

  /**
   * Check if file is an image
   */
  const isImage = (doc) => {
    const mimeType = doc.fileType || doc.mimeType || '';
    return mimeType.startsWith('image/');
  };

  /**
   * Get filtered documents based on view mode
   */
  const getFilteredDocuments = () => {
    if (viewMode === 'gallery') {
      // Gallery view only shows images
      return documents.filter(doc => doc.type === 'folder' || isImage(doc));
    }
    return documents;
  };

  /**
   * Apply search filter to documents
   */
  const applySearch = (docs) => {
    if (!searchQuery.trim()) return docs;

    const query = searchQuery.toLowerCase();
    return docs.filter(doc => {
      const name = (doc.title || doc.fileName || doc.name || '').toLowerCase();
      const type = doc.type || '';
      return name.includes(query) || type.includes(query);
    });
  };

  /**
   * Apply sorting to documents
   */
  const applySorting = (docs) => {
    const sorted = [...docs].sort((a, b) => {
      // Always keep folders at the top
      if (a.type === 'folder' && b.type !== 'folder') return -1;
      if (a.type !== 'folder' && b.type === 'folder') return 1;

      let comparison = 0;

      switch (sortBy) {
        case 'name':
          const nameA = (a.title || a.fileName || a.name || '').toLowerCase();
          const nameB = (b.title || b.fileName || b.name || '').toLowerCase();
          comparison = nameA.localeCompare(nameB);
          break;

        case 'date':
          const dateA = (a.updatedAt || a.createdAt)?.toMillis?.() || 0;
          const dateB = (b.updatedAt || b.createdAt)?.toMillis?.() || 0;
          comparison = dateA - dateB;
          break;

        case 'size':
          const sizeA = a.fileSize || a.size || 0;
          const sizeB = b.fileSize || b.size || 0;
          comparison = sizeA - sizeB;
          break;

        case 'type':
          const typeA = getFileTypeLabel(a);
          const typeB = getFileTypeLabel(b);
          comparison = typeA.localeCompare(typeB);
          break;

        default:
          comparison = 0;
      }

      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return sorted;
  };

  /**
   * Get processed documents (filtered, searched, sorted)
   */
  const getProcessedDocuments = () => {
    let docs = getFilteredDocuments();
    docs = applySearch(docs);
    docs = applySorting(docs);
    return docs;
  };

  /**
   * Toggle sort order
   */
  const toggleSortOrder = () => {
    setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
  };

  /**
   * Open image in lightbox
   */
  const handleImageClick = (clickedDoc) => {
    // Get all images in current folder
    const currentFolderDocs = getProcessedDocuments().filter((doc) => {
      const docFolder = doc.folderPath || '/';
      const current = currentPath || '/';
      return docFolder === current && doc.type !== 'folder' && isImage(doc);
    });

    // Find index of clicked image
    const index = currentFolderDocs.findIndex(doc => doc.id === clickedDoc.id);

    if (index !== -1) {
      setLightboxImages(currentFolderDocs);
      setLightboxIndex(index);
      setLightboxOpen(true);
    }
  };

  /**
   * Toggle member selection for sharing
   */
  const toggleMemberSelection = (memberId) => {
    setSelectedMembersToShare(prev => {
      if (prev.includes(memberId)) {
        return prev.filter(id => id !== memberId);
      }
      return [...prev, memberId];
    });
  };

  /**
   * Toggle group selection for sharing
   */
  const toggleGroupSelection = (groupId) => {
    setSelectedGroupsToShare(prev => {
      if (prev.includes(groupId)) {
        return prev.filter(id => id !== groupId);
      }
      return [...prev, groupId];
    });
  };

  /**
   * Format file size
   */
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  /**
   * Format date
   */
  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  if (!currentUser) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Please sign in to access the vault.</Alert>
      </Box>
    );
  }

  if (!profile?.familyCode) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Please join a family to access the vault.</Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{ p: 3, position: 'relative' }}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, mb: 3 }}>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <LockIcon color="primary" />
            <Typography variant="h5">
              {title}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Secure documents with granular sharing controls
          </Typography>

          {/* Breadcrumb Navigation */}
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" />}
            aria-label="breadcrumb"
            sx={{ mt: 1 }}
          >
            <Link
              component="button"
              variant="body2"
              onClick={() => setCurrentPath('/')}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                color: currentPath === '/' ? 'primary.main' : 'text.primary',
                fontWeight: currentPath === '/' ? 600 : 400,
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' }
              }}
            >
              <HomeIcon fontSize="small" />
              Root
            </Link>
            {getBreadcrumbs().map((segment, index) => {
              const isLast = index === getBreadcrumbs().length - 1;
              return (
                <Link
                  key={index}
                  component="button"
                  variant="body2"
                  onClick={() => handleBreadcrumbClick(index)}
                  sx={{
                    color: isLast ? 'primary.main' : 'text.primary',
                    fontWeight: isLast ? 600 : 400,
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' }
                  }}
                >
                  {segment}
                </Link>
              );
            })}
          </Breadcrumbs>
        </Box>

        {/* Toolbar Buttons */}
        <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' }, width: { xs: '100%', sm: 'auto' } }}>
          <Button
            variant="outlined"
            startIcon={<CreateFolderIcon />}
            onClick={() => setFolderDialogOpen(true)}
            size="small"
          >
            New Folder
          </Button>
          <Button
            variant="contained"
            startIcon={<UploadIcon />}
            onClick={() => setUploadDialogOpen(true)}
            size="small"
          >
            {uploadButtonText}
          </Button>
        </Box>
      </Box>

      {/* Search, Sort, and View Controls */}
      <Box sx={{
        display: 'flex',
        gap: 2,
        mb: 2,
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'stretch' : 'center',
        justifyContent: 'space-between'
      }}>
        {/* Search Field */}
        <TextField
          size="small"
          placeholder="Search files and folders..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{
            minWidth: isMobile ? '100%' : 250,
            maxWidth: isMobile ? '100%' : 400
          }}
        />

        {/* Sort and View Controls */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {/* Sort Dropdown */}
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel id="vault-sort-by-label">Sort by</InputLabel>
            <Select
              labelId="vault-sort-by-label"
              value={sortBy}
              label="Sort by"
              onChange={(e) => setSortBy(e.target.value)}
            >
              <MenuItem value="date">Date</MenuItem>
              <MenuItem value="name">Name</MenuItem>
              <MenuItem value="size">Size</MenuItem>
              <MenuItem value="type">Type</MenuItem>
            </Select>
          </FormControl>

          {/* Sort Order Toggle */}
          <IconButton
            size="small"
            onClick={toggleSortOrder}
            title={sortOrder === 'asc' ? 'Ascending' : 'Descending'}
          >
            {sortOrder === 'asc' ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
          </IconButton>

          {/* View Mode Toggle */}
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewModeChange}
            size="small"
            aria-label="view mode"
          >
            <ToggleButton value="list" aria-label="list view">
              <ViewListIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value="grid" aria-label="grid view">
              <ViewModuleIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value="gallery" aria-label="gallery view">
              <ViewComfyIcon fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {/* Error display */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Loading state */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <LinearProgress sx={{ width: '100%' }} />
        </Box>
      )}

      {/* Documents Display */}
      {!loading && documents.length === 0 && (
        <Alert severity="info" icon={<LockIcon />}>
          {emptyMessage}
        </Alert>
      )}

      {!loading && documents.length > 0 && (() => {
        const filteredDocs = getProcessedDocuments().filter((doc) => {
          const docFolder = doc.folderPath || '/';
          const current = currentPath || '/';
          return docFolder === current;
        });

        if (filteredDocs.length === 0 && viewMode === 'gallery') {
          return (
            <Alert severity="info" sx={{ mt: 2 }}>
              No images in this folder. Switch to List or Grid view to see all files.
            </Alert>
          );
        }

        // LIST VIEW
        if (viewMode === 'list') {
          return (
            <TableContainer component={Paper} sx={{
              mt: 2,
              overflowX: 'auto',
              '& .MuiTable-root': {
                minWidth: isMobile ? 500 : 650
              }
            }}>
              <Table size={isMobile ? 'medium' : 'small'}>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell align="right">Size</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Shared</TableCell>
                    <TableCell>Date Modified</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredDocs.map((doc) => (
                    <TableRow
                      key={doc.id}
                      hover
                      sx={{
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: 'action.hover' }
                      }}
                    >
                      <TableCell
                        onClick={() => {
                          if (doc.type === 'folder') {
                            handleFolderClick(doc);
                          } else if (isImage(doc)) {
                            handleImageClick(doc);
                          } else {
                            handleDownload(doc);
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {doc.type === 'folder' ? (
                            <FolderIcon color="primary" fontSize="small" />
                          ) : (
                            <FileIcon color="action" fontSize="small" />
                          )}
                          <Typography variant="body2" noWrap sx={{ maxWidth: { xs: 140, sm: 250 } }}>
                            {doc.title || doc.fileName || doc.name || 'Untitled'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="text.secondary">
                          {doc.type === 'folder' ? '-' : formatFileSize(doc.fileSize || doc.size || 0)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {doc.type === 'folder' ? 'Folder' : getFileTypeLabel(doc)}
                        </Typography>
                      </TableCell>
                      <TableCell
                        onClick={(e) => {
                          e.stopPropagation();
                          if (doc.type !== 'folder' && doc.sharedWith && doc.sharedWith.length > 0) {
                            handleOpenManageAccess(doc);
                          }
                        }}
                        sx={{
                          cursor: (doc.type !== 'folder' && doc.sharedWith && doc.sharedWith.length > 0) ? 'pointer' : 'default'
                        }}
                      >
                        {doc.sharedWith && doc.sharedWith.length > 0 ? (
                          <Chip
                            icon={<PersonIcon fontSize="small" />}
                            label={`${doc.sharedWith.length} ${doc.sharedWith.length === 1 ? 'person' : 'people'}`}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ '&:hover': { bgcolor: 'action.hover' } }}
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(doc.updatedAt || doc.createdAt)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size={isMobile ? 'medium' : 'small'}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMenuOpen(e, doc);
                          }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          );
        }

        // GRID VIEW
        if (viewMode === 'grid') {
          return (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {filteredDocs.map((doc) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={doc.id}>
                  <Card
                    sx={{
                      height: '100%',
                      cursor: 'pointer',
                      '&:hover': { boxShadow: 4 }
                    }}
                    onClick={() => {
                      if (doc.type === 'folder') {
                        handleFolderClick(doc);
                      } else if (isImage(doc)) {
                        handleImageClick(doc);
                      } else {
                        handleDownload(doc);
                      }
                    }}
                  >
                    <CardContent sx={{ textAlign: 'center', pb: 1 }}>
                      <Box sx={{ mb: 2, mt: 1 }}>
                        {doc.type === 'folder' ? (
                          <FolderIcon color="primary" sx={{ fontSize: 64 }} />
                        ) : isImage(doc) && doc.fileUrl ? (
                          <Box
                            component="img"
                            src={doc.fileUrl}
                            alt={doc.fileName || 'Image'}
                            sx={{
                              width: '100%',
                              height: 120,
                              objectFit: 'cover',
                              borderRadius: 1
                            }}
                          />
                        ) : (
                          <FileIcon color="action" sx={{ fontSize: 64 }} />
                        )}
                      </Box>
                      <Typography
                        variant="body2"
                        fontWeight={500}
                        noWrap
                        title={doc.title || doc.fileName || doc.name}
                      >
                        {doc.title || doc.fileName || doc.name || 'Untitled'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {doc.type === 'folder' ? 'Folder' : formatFileSize(doc.fileSize || 0)}
                      </Typography>
                      {doc.sharedWith && doc.sharedWith.length > 0 && (
                        <Chip
                          icon={<PersonIcon fontSize="small" />}
                          label={doc.sharedWith.length}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ mt: 1 }}
                        />
                      )}
                    </CardContent>
                    <CardActions sx={{ justifyContent: 'center', pt: 0 }}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMenuOpen(e, doc);
                        }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          );
        }

        // GALLERY VIEW (Images only)
        if (viewMode === 'gallery') {
          return (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {filteredDocs.map((doc) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={doc.id}>
                  <Card
                    sx={{
                      height: '100%',
                      cursor: 'pointer',
                      '&:hover': { boxShadow: 6 }
                    }}
                    onClick={() => {
                      if (doc.type === 'folder') {
                        handleFolderClick(doc);
                      } else {
                        handleImageClick(doc);
                      }
                    }}
                  >
                    <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                      {doc.type === 'folder' ? (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                          <FolderIcon color="primary" sx={{ fontSize: 80 }} />
                          <Typography variant="body2" fontWeight={500} sx={{ mt: 1, px: 2 }}>
                            {doc.name || 'Folder'}
                          </Typography>
                        </Box>
                      ) : (
                        <>
                          <Box
                            component="img"
                            src={doc.fileUrl || ''}
                            alt={doc.fileName || 'Image'}
                            sx={{
                              width: '100%',
                              height: 200,
                              objectFit: 'cover'
                            }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                          <Box
                            sx={{
                              display: 'none',
                              width: '100%',
                              height: 200,
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: 'action.hover'
                            }}
                          >
                            <ImageIcon sx={{ fontSize: 80, color: 'action.disabled' }} />
                          </Box>
                          <Box sx={{ p: 1 }}>
                            <Typography variant="caption" fontWeight={500} noWrap>
                              {doc.fileName || 'Image'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              {formatFileSize(doc.fileSize || 0)}
                            </Typography>
                            {doc.sharedWith && doc.sharedWith.length > 0 && (
                              <Chip
                                icon={<PersonIcon fontSize="small" />}
                                label={`${doc.sharedWith.length} ${doc.sharedWith.length === 1 ? 'person' : 'people'}`}
                                size="small"
                                color="primary"
                                variant="outlined"
                                sx={{ mt: 0.5 }}
                              />
                            )}
                          </Box>
                        </>
                      )}
                    </CardContent>
                    <CardActions sx={{ justifyContent: 'flex-end', pt: 0, px: 1, pb: 1 }}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMenuOpen(e, doc);
                        }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          );
        }

        return null;
      })()}

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        {menuItem?.type !== 'folder' && (
          <MenuItem onClick={() => {
            handleDownload(menuItem);
            handleMenuClose();
          }}>
            <DownloadIcon sx={{ mr: 1 }} fontSize="small" />
            Download
          </MenuItem>
        )}
        {menuItem?.type !== 'folder' && (
          <MenuItem onClick={() => {
            setSelectedFile(menuItem);
            setShareDialogOpen(true);
            handleMenuClose();
          }}>
            <ShareIcon sx={{ mr: 1 }} fontSize="small" />
            Share
          </MenuItem>
        )}
        {menuItem?.type !== 'folder' && menuItem?.sharedWith && menuItem.sharedWith.length > 0 && (
          <MenuItem onClick={() => {
            handleOpenManageAccess(menuItem);
            handleMenuClose();
          }}>
            <PersonIcon sx={{ mr: 1 }} fontSize="small" />
            Manage Access
          </MenuItem>
        )}
        <MenuItem onClick={() => {
          setSelectedFile(menuItem);
          setRenameValue(menuItem?.name || menuItem?.fileName || '');
          setRenameDialogOpen(true);
          handleMenuClose();
        }}>
          <EditIcon sx={{ mr: 1 }} fontSize="small" />
          Rename
        </MenuItem>
        <MenuItem onClick={() => {
          setSelectedFile(menuItem);
          setMoveDialogOpen(true);
          handleMenuClose();
        }}>
          <MoveIcon sx={{ mr: 1 }} fontSize="small" />
          Move
        </MenuItem>
        {isAdmin && menuItem?.type !== 'folder' && (
          <MenuItem onClick={() => {
            handleCopyToFamily(menuItem);
            handleMenuClose();
          }}>
            <CopyIcon sx={{ mr: 1 }} fontSize="small" color="secondary" />
            Copy to Family Documents
          </MenuItem>
        )}
        <MenuItem onClick={() => {
          setSelectedFile(menuItem);
          setDeleteDialogOpen(true);
          handleMenuClose();
        }}>
          <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
          Delete
        </MenuItem>
      </Menu>

      {/* Upload Dialog */}
      <Dialog
        open={uploadDialogOpen}
        onClose={() => !uploading && setUploadDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Upload to Vault</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="caption">
              {fileTypeFilter === 'image/*'
                ? 'Photos uploaded to the Vault are private. You can share them with specific family members.'
                : 'Documents uploaded to the Vault are private. You can share them with specific family members.'}
            </Typography>
          </Alert>
          <Typography variant="caption" color="text.secondary">
            Maximum file size: 100MB
          </Typography>
          <Box sx={{ mt: 2, mb: selectedFiles.length > 0 ? 1 : 2 }}>
            <input
              type="file"
              multiple
              accept={fileTypeFilter || undefined}
              onChange={(e) => setSelectedFiles(Array.from(e.target.files))}
              style={{ width: '100%' }}
            />
          </Box>
          {selectedFiles.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Selected files ({selectedFiles.length}):
              </Typography>
              {selectedFiles.map((file, index) => (
                <Typography key={index} variant="caption" display="block">
                  • {file.name} ({formatFileSize(file.size)})
                </Typography>
              ))}
            </Box>
          )}
          <FormControl fullWidth size="small" sx={{ mb: 1 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={uploadCategory}
              label="Category"
              onChange={(e) => setUploadCategory(e.target.value)}
            >
              {fileTypeFilter === 'image/*' ? [
                <MenuItem key="general" value="general">General</MenuItem>,
                <MenuItem key="family-events" value="family-events">Family Events</MenuItem>,
                <MenuItem key="vacations" value="vacations">Vacations</MenuItem>,
                <MenuItem key="holidays" value="holidays">Holidays</MenuItem>,
                <MenuItem key="celebrations" value="celebrations">Celebrations</MenuItem>,
                <MenuItem key="portraits" value="portraits">Portraits</MenuItem>,
                <MenuItem key="sensitive" value="sensitive">Sensitive</MenuItem>,
                <MenuItem key="other" value="other">Other</MenuItem>
              ] : [
                <MenuItem key="general" value="general">General</MenuItem>,
                <MenuItem key="legal" value="legal">Legal</MenuItem>,
                <MenuItem key="financial" value="financial">Financial</MenuItem>,
                <MenuItem key="health" value="health">Health</MenuItem>,
                <MenuItem key="medical" value="medical">Medical</MenuItem>,
                <MenuItem key="personal" value="personal">Personal</MenuItem>,
                <MenuItem key="sensitive" value="sensitive">Sensitive</MenuItem>,
                <MenuItem key="other" value="other">Other</MenuItem>
              ]}
            </Select>
          </FormControl>
          {uploading && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress variant="determinate" value={uploadProgress} />
              <Typography variant="caption" sx={{ mt: 1 }}>
                Uploading... {uploadProgress}%
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)} disabled={uploading}>
            Cancel
          </Button>
          <Button
            onClick={() => handleUpload(selectedFiles)}
            disabled={uploading || selectedFiles.length === 0}
            variant="contained"
          >
            Upload
          </Button>
        </DialogActions>
      </Dialog>

      {/* Share Dialog */}
      <Dialog
        open={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Share "{selectedFile?.name}"</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Select family members or groups to share this document with.
          </Typography>

          {/* Permission level selector */}
          <FormControl fullWidth sx={{ mt: 2, mb: 3 }}>
            <InputLabel>Permission Level</InputLabel>
            <Select
              value={sharePermission}
              onChange={(e) => setSharePermission(e.target.value)}
              label="Permission Level"
            >
              <MenuItem value="read">View Only</MenuItem>
              <MenuItem value="write">Can Edit</MenuItem>
            </Select>
          </FormControl>

          <Divider sx={{ my: 2 }} />

          {/* Family Members */}
          <Typography variant="subtitle2" gutterBottom>
            <PersonIcon sx={{ verticalAlign: 'middle', mr: 1 }} fontSize="small" />
            Family Members
          </Typography>
          <List sx={{ maxHeight: 200, overflow: 'auto', mb: 2 }}>
            {familyMembers.map((member) => (
              <ListItem
                key={member.id}
                button
                onClick={() => toggleMemberSelection(member.id)}
              >
                <ListItemIcon>
                  <Checkbox
                    checked={selectedMembersToShare.includes(member.id)}
                    edge="start"
                  />
                </ListItemIcon>
                <ListItemIcon>
                  <Avatar sx={{ width: 32, height: 32 }}>
                    {member.firstName?.[0]}{member.lastName?.[0]}
                  </Avatar>
                </ListItemIcon>
                <ListItemText
                  primary={`${member.firstName} ${member.lastName}`}
                  secondary={member.email}
                />
              </ListItem>
            ))}
          </List>

          <Divider sx={{ my: 2 }} />

          {/* Family Groups */}
          <Typography variant="subtitle2" gutterBottom>
            <GroupIcon sx={{ verticalAlign: 'middle', mr: 1 }} fontSize="small" />
            Family Groups
          </Typography>
          <List sx={{ maxHeight: 200, overflow: 'auto' }}>
            {familyGroups.map((group) => (
              <ListItem
                key={group.id}
                button
                onClick={() => toggleGroupSelection(group.id)}
              >
                <ListItemIcon>
                  <Checkbox
                    checked={selectedGroupsToShare.includes(group.id)}
                    edge="start"
                  />
                </ListItemIcon>
                <ListItemText
                  primary={group.name}
                  secondary={`${group.members?.length || 0} members`}
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setShareDialogOpen(false);
            setSelectedMembersToShare([]);
            setSelectedGroupsToShare([]);
          }}>
            Cancel
          </Button>
          <Button
            onClick={handleShare}
            variant="contained"
            disabled={selectedMembersToShare.length === 0 && selectedGroupsToShare.length === 0}
          >
            Share
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        fullScreen={isMobile}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{selectedFile?.name}"?
          </Typography>
          <Typography variant="caption" color="text.secondary">
            This will remove it from the vault and all sharing will be revoked.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Folder Dialog */}
      <Dialog
        open={folderDialogOpen}
        onClose={() => setFolderDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Create New Folder in Vault</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Folder Name"
            fullWidth
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleCreateFolder();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFolderDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateFolder}
            variant="contained"
            disabled={!newFolderName.trim()}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manage Access Dialog */}
      <Dialog
        open={manageAccessDialogOpen}
        onClose={() => setManageAccessDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonIcon color="primary" />
            Manage Access - "{selectedFile?.title || selectedFile?.fileName || selectedFile?.name}"
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Users who have access to this document:
          </Typography>

          {currentShares.length === 0 ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              This document is not shared with anyone yet.
            </Alert>
          ) : (
            <List sx={{ mt: 2 }}>
              {currentShares.map((share) => (
                <ListItem
                  key={share.userId}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    mb: 1
                  }}
                >
                  <ListItemIcon>
                    <Avatar sx={{ width: 40, height: 40, bgcolor: 'primary.main' }}>
                      {share.userName.split(' ').map(n => n[0]).join('')}
                    </Avatar>
                  </ListItemIcon>
                  <ListItemText
                    primary={share.userName}
                    secondary={share.userEmail}
                  />
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <Select
                        value={share.permission}
                        onChange={(e) => handleUpdatePermission(share.userId, e.target.value)}
                      >
                        <MenuItem value="read">View Only</MenuItem>
                        <MenuItem value="edit">Can Edit</MenuItem>
                        <MenuItem value="share">Can Share</MenuItem>
                      </Select>
                    </FormControl>
                    <Tooltip title="Revoke Access">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRevokeAccess(share.userId)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </ListItem>
              ))}
            </List>
          )}

          <Divider sx={{ my: 2 }} />

          <Button
            variant="outlined"
            startIcon={<ShareIcon />}
            fullWidth
            onClick={() => {
              setManageAccessDialogOpen(false);
              setShareDialogOpen(true);
            }}
          >
            Share with More People
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManageAccessDialogOpen(false)}>
            Done
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rename Dialog */}
      <Dialog
        open={renameDialogOpen}
        onClose={() => setRenameDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          Rename {selectedFile?.type === 'folder' ? 'Folder' : 'File'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="New Name"
            fullWidth
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleRename();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleRename}
            variant="contained"
            disabled={!renameValue.trim()}
          >
            Rename
          </Button>
        </DialogActions>
      </Dialog>

      {/* Drag & Drop Overlay */}
      {isDragging && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(25, 118, 210, 0.1)',
            border: '3px dashed',
            borderColor: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            pointerEvents: 'none'
          }}
        >
          <Paper
            elevation={8}
            sx={{
              p: 4,
              backgroundColor: 'background.paper',
              borderRadius: 2,
              textAlign: 'center'
            }}
          >
            <CloudUploadIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
            <Typography variant="h5" color="primary" gutterBottom>
              Drop files here to upload to vault
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Upload to: {currentPath === '/' ? 'Root' : currentPath}
            </Typography>
          </Paper>
        </Box>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Image Lightbox */}
      <ImageLightbox
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        images={lightboxImages}
        currentIndex={lightboxIndex}
        onNavigate={setLightboxIndex}
      />

      {/* Folder Picker Dialog for Move */}
      <FolderPickerDialog
        open={moveDialogOpen}
        onClose={() => {
          setMoveDialogOpen(false);
          setSelectedFile(null);
        }}
        onSelect={handleMove}
        allDocuments={documents}
        currentPath={selectedFile?.folderPath || '/'}
        title="Move to Folder"
      />
    </Box>
  );
};

export default Vault;
